/**
 * Ableton device poller - keeps `ableton/deviceState.ts` honest about which
 * named devices are switched on.
 *
 * # This poller has TWO halves, unlike every other one
 *
 * `obs/scenePoll.ts` and the engine pollers just READ. This one has to ask
 * first: `OP-ButtonsDevice` is request-driven for a name it has never seen,
 * so nothing appears in OPBridge's store until somebody sends
 * `get_device_on`. So a tick is:
 *
 *   1. **ASK** - one `get_device_on` per distinct target on the current page,
 *      over `POST /osc/send` to the device's loopback port.
 *   2. **READ** - one `GET /devices/status` per bridge, into the mirror.
 *
 * After the first successful resolve the device ADDS A WATCH and pushes on
 * change by itself (`OP-ButtonsDevice.js::addWatch`), so a mouse click in
 * Live updates without us asking again. The ask therefore exists to seed a
 * name and to recover one the device dropped (its watch table is LRU-capped
 * at 32) - not to sample. That is why the interval can be slow.
 *
 * # Why demand-gating matters here even though the READ is cheap
 *
 * `GET /devices/status` is an in-memory read with no socket behind it, so the
 * read half would be nearly free at any cadence. The ASK half is not: every
 * `get_device_on` walks the LOM device tree on the Mac
 * (`OP-ButtonsDevice.js::walkTrack`) inside Live's own process, during a gig.
 * So the same three gates as the OBS poller apply, and for a sharper reason:
 *
 * 1. Only bridges an Ableton Device control actually points at.
 * 2. Only while the page is visible.
 * 3. Only targets on the CURRENT page.
 *
 * # A button with no bridge contributes NOTHING, deliberately
 *
 * Same rule as the OBS poller, but the consequence of breaking it is worse.
 * `POST /osc/send` refuses every non-loopback target, so the OSC never
 * crosses the LAN - which means `127.0.0.1:11280` is valid on EVERY machine
 * and a send to the wrong bridge is an error NOWHERE in the stack: OPBridge
 * answers `{ok:true}` and UDP drops it. OBS at least got a 503. So an unaimed
 * button is refused at dispatch and must not be polled here either - reading
 * the default bridge would put live-looking state under a control that cannot
 * fire, from the wrong machine's Live set.
 */

import { getConnection, bridgeStateKey } from '../opbridge/store.ts';
import { layoutItems } from '../layout/store.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';
import { recordDevicesStatus, recordDevicesFailure } from './deviceState.ts';
import { effectiveNth } from './deviceControl.ts';
import type { AbletonDeviceTarget } from '../layout/projectStore.ts';

/** Where `OP-ButtonsDevice v0.1-rc2` listens, relative to the bridge that
 *  fires. **Loopback is not a convenience here, it is the contract** - see
 *  the file header on why an unaimed send is invisible. Moved from 11270 on
 *  2026-09-19: `OP-SceneStop` already binds 11270 on the operator's Mac, and
 *  it lives in no repo, so three independent repo sweeps all reported that
 *  port free. */
export const BUTTONS_DEVICE_TARGET = '127.0.0.1:11280';

export const GET_DEVICE_ON_ADDRESS = '/OP-ButtonsDevice/get_device_on';
export const SET_DEVICE_ON_ADDRESS = '/OP-ButtonsDevice/set_device_on';

/** Correction + re-seed cadence. Slow on purpose: the device pushes changes
 *  on its own once a name is watched, so this is not a sampler. */
const POLL_INTERVAL_MS = 15_000;

/** Tick length when there is nothing to ask about. Shorter than the poll so
 *  placing the first Ableton Device button does not leave the operator
 *  staring at an unknown state for 15s. */
const IDLE_INTERVAL_MS = 4_000;

let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = false;

/** Start the poller. Idempotent. */
export function startAbletonDevicePoll(): void {
  if (timer !== null) return;
  stopped = false;
  schedule(0);
}

/** Stop the poller. Used by tests + on tear-down. */
export function stopAbletonDevicePoll(): void {
  stopped = true;
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

function schedule(delayMs: number): void {
  if (stopped) return;
  timer = setTimeout(() => {
    timer = null;
    void tick();
  }, delayMs);
}

/** One target to ask about, already paired with the bridge that must ask. */
export interface DeviceQuery {
  bridgeId: string;
  target: AbletonDeviceTarget;
}

/**
 * Every (bridge, target) pair an Ableton Device control on the CURRENT page
 * names, de-duplicated.
 *
 * De-duplication is by the same key the device watches on - bridge, track,
 * device name and `nth` - so three buttons pointing at one device cost one
 * LOM walk per tick rather than three. Incomplete targets are skipped: they
 * are refused at dispatch and show a fault on their face, so asking Live
 * about a half-configured button would buy nothing.
 */
export function deviceQueriesInUse(): DeviceQuery[] {
  const seen = new Set<string>();
  const out: DeviceQuery[] = [];
  for (const item of layoutItems()) {
    if (item.kind !== 'button') continue;
    if (item.actionType !== 'abletonDevice') continue;
    const target = item.abletonDevice;
    if (!target?.trackName || !target.deviceName) continue;
    for (const aimed of item.bridgeIds ?? []) {
      // Row id, not the stored node id - see `bridgeStateKey`.
      const bridgeId = bridgeStateKey(aimed);
      // Separator is an explicit \u0000 escape, NOT a literal NUL byte. A
      // literal one makes this file read as BINARY to grep, which silently
      // skips it - an invisible blind spot in every future search. It has to
      // be a character that cannot occur in an operator-typed track or device
      // name, or two different targets collide into one key and one of them
      // is never polled.
      const key = [bridgeId, target.trackName, target.deviceName, effectiveNth(target)]
        .join('\u0000');
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ bridgeId, target });
    }
  }
  return out;
}

/** Bridge ids named by any complete Ableton Device control on this page. */
export function abletonDeviceBridgeIdsInUse(): string[] {
  return Array.from(new Set(deviceQueriesInUse().map((q) => q.bridgeId)));
}

/**
 * Ask Live about one target. Never throws.
 *
 * A failure here is deliberately NOT recorded as a device-state failure: the
 * ask and the read are different calls with different failure modes, and a
 * refused `/osc/send` says nothing about whether the last READ is still
 * valid. The read half owns the mirror's status.
 */
export async function askDeviceState(query: DeviceQuery): Promise<void> {
  const conn = getConnection(query.bridgeId);
  if (!conn || conn.status() !== 'connected') return;
  try {
    await conn.httpClient().oscSend(
      GET_DEVICE_ON_ADDRESS,
      [query.target.trackName, query.target.deviceName, effectiveNth(query.target)],
      BUTTONS_DEVICE_TARGET,
    );
  } catch {
    // Swallowed on purpose - see the doc comment. The next READ reports the
    // state of the world either way.
  }
}

/**
 * Read one bridge's device status now and record the outcome.
 *
 * Exported because the Inspector calls it directly: opening the panel on an
 * Ableton Device button must not wait up to a poll interval to say whether
 * the device is even loaded. Never throws - every failure becomes a named
 * status on the bridge.
 */
export async function refreshDeviceStatus(bridgeId: string): Promise<void> {
  if (!bridgeId) return;
  const conn = getConnection(bridgeId);
  if (!conn) {
    recordDevicesFailure(bridgeId, 'error', 'No such bridge.');
    return;
  }
  if (conn.status() !== 'connected') {
    recordDevicesFailure(bridgeId, 'error', `Bridge is ${conn.status()}.`);
    return;
  }
  try {
    recordDevicesStatus(bridgeId, await conn.httpClient().devicesStatus());
  } catch (err) {
    const status = err instanceof OpbridgeError ? err.httpStatus : undefined;
    const msg = err instanceof OpbridgeError ? err.message
              : err instanceof Error           ? err.message
              : String(err);
    // Three causes, three owners, three fixes - kept apart all the way to
    // the face, exactly as the OBS poller does.
    if (status === 401) {
      recordDevicesFailure(bridgeId, 'unauthorized',
        'This bridge pairing predates the /devices/status scope (opbuttons-full v16). Re-pair this computer to re-mint the token.');
    } else if (status === 404) {
      recordDevicesFailure(bridgeId, 'error',
        'This OPBridge is too old to have /devices/status.');
    } else {
      recordDevicesFailure(bridgeId, 'error', msg);
    }
  }
}

function pageIsVisible(): boolean {
  // `document` is absent in the pure-logic smokes' Node shim, and a poller
  // that refused to run there would make itself untestable.
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

async function tick(): Promise<void> {
  if (stopped) return;
  if (!pageIsVisible()) {
    schedule(IDLE_INTERVAL_MS);
    return;
  }
  const queries = deviceQueriesInUse();
  if (queries.length === 0) {
    schedule(IDLE_INTERVAL_MS);
    return;
  }

  // ASK, then READ. The order is the point: a read taken before the asks
  // land would miss every newly-placed button by a full interval, and on a
  // freshly-opened page that is the whole content of the surface.
  //
  // Sequential, not `Promise.all`: each ask walks the LOM tree inside Live's
  // process, and firing a burst of those at a machine that is also playing
  // the gig is the cost this poller is shaped to avoid.
  for (const q of queries) {
    if (stopped) return;
    await askDeviceState(q);
  }

  for (const id of new Set(queries.map((q) => q.bridgeId))) {
    if (stopped) return;
    await refreshDeviceStatus(id);
  }
  schedule(POLL_INTERVAL_MS);
}
